/* AdvisoryViewComponent.js */

const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
//const UserGridData = require('@gluenetworks/entity_grid_gl').UserData;

const advisoryDateColumn = require("./column_renderers/date_column/DateComponent");
const advisoryCriticalityColumn = require("./column_renderers/advisory_criticality/AdvisoryCriticalityComponent");
const advisoryReadMoreColumn = require("./column_renderers/advisory_detail/AdvisoryDetailComponent");
const advisorySourceColumn = require("./column_renderers/source/SourceComponent");

const Renderer = require("./AdvisoryViewRenderer");
const uString = require("underscore.string");

const PagingAdvisoryFeed = require("./PagingAdvisoryFeed");
const PlatformSdk = require("@gluenetworks/platform-sdk-remote");
const {applyExposureLicenseGate} = require("../../../../lib/exposureLicenseGate");

const ExposureManagementPrefsEntity = PlatformSdk.entities.ws.ExposureManagementPrefs;

_.mixin(uString.exports());

class AdvisoryViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        io,
        utils,
        userSession,
        storageFactory,
        solutionsManager,
        MUF,
        vendorApiConfig
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._steveIo = io;
        this._steveUtils = utils;
        this._userSession = userSession;
        this._modalUserFeedback = MUF;
        this._vendorApiConfig = vendorApiConfig;

        this._solutionName =
            (solutionsManager.activeSolution && solutionsManager.activeSolution.name) || "";
        this._gridKey = _.camelCase(this._solutionName) + "_" + "advisoryView";
        this._userGridData = storageFactory.getRemoteStorageInstanceForEntityGrid(
            "entity_grid_gl",
            this._gridKey
        ); //new UserGridData(this._gridKey);

        this._entity = undefined;
        this._pagingFeed = undefined;

        this.columnConfig = {
            sir: {
                label: "Criticality",
                path: "data.sir",
                columnRendererComponent: advisoryCriticalityColumn,
                sort: "asc",
                priority: 1,
                width: 100,
            },
            displayName: {
                label: "Advisory ID",
                priority: 2,
                shown: true,
                width: 270,
            },
            source: {
                label: "Source",
                priority: 3,
                shown: true,
                width: 70,
                columnRendererComponent: advisorySourceColumn,
            },
            firstPublished: {
                sort: "desc",
                label: "First Found",
                path: "data.firstPublished",
                columnRendererComponent: advisoryDateColumn,
                priority: 3,
                width: 160,
            },
            advisoryTitle: {
                label: "Title",
                path: "data.advisoryTitle",
                priority: 4,
                width: 400,
            },
            firstFixed: {
                sort: "desc",
                label: "First Fixed",
                path: "data.firstFixed",
                priority: 5,
                width: 100,
            },
            cves: {
                label: "CVEs",
                path: "data.cves",
                sortable: false,
                priority: 6,
                shown: true,
            },
            readMore: {
                entityColumn: true,
                columnRendererComponent: advisoryReadMoreColumn,
                resizable: false,
                sortable: false,
                width: 45,
                priority: 7,
            },
            summary: {
                label: "Summary",
                path: "data.summary",
                sortable: false,
                priority: 8,
                shown: false,
            },
            bugIDs: {
                label: "Bug IDs",
                path: "data.bugIDs",
                sortable: false,
                priority: 9,
                shown: false,
            },
        };

        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _patchSorting(sorting) {
        const patchedSorting = _.clone(sorting);
        if (
            patchedSorting &&
            Object.keys(patchedSorting).length === 1 &&
            !patchedSorting.firstPublished
        ) {
            patchedSorting.firstPublished = -1;
        }
        return patchedSorting;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenFeedReady() {
        return this._pagingFeed.whenFeedReady();
    }

    wasFeedPreviouslyInitialized() {
        return this._pagingFeed.wasPreviouslyInitialized();
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();

        return this._renderer
            .shutdown()
            .then(() => {
                if (!_.isUndefined(this._pagingFeed)) {
                    return this._pagingFeed.shutdown();
                } else {
                    return Promise.resolve();
                }
            })
            .then(
                () => {
                    this._logger = undefined;
                    this._gameEditor = undefined;
                    this._renderer = undefined;
                    this._steveIo = undefined;
                    this._steveUtils = undefined;
                    this._entity = undefined;
                    this._pagingFeed = undefined;
                    this._vendorApiConfig = undefined;
                },
                (err) => {
                    this._logger.warn({error: err}, "AdvisoryViewComponent.shutdown() error.");
                }
            );
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    get entityInstanceId() {
        // TODO To be reviewed -- Required
        return this._entityInstanceId;
    }

    whenModelIsReady() {
        return this._gameEditor
            .whenDeviceIsReady()
            .then(() => {
                const userSorting = this._userGridData.getSorting() || {};
                const patchedSort = this._patchSorting(userSorting);

                this._entity = this._gameEditor.getOsVersion();

                if (!this._entity) {
                    return Promise.reject("NoOS");
                }

                this._pagingFeed = new PagingAdvisoryFeed({
                    feedKey: this._gridKey + "_feed",
                    userSession: this._userSession,
                    entityDef: this._entity,
                    columnConfig: this.columnConfig,
                    additionalDataFields: [],
                    sort: patchedSort,
                });
                this._registerListeners();
                return this._pagingFeed.whenFeedReady();
            })
            .catch((err) => {
                let errorCode = err.errorCode;
                let errorMsg = "This Advisory Summary cannot be opened.";

                if (
                    _.get(this._userSession, "authorizedActions", []).indexOf(
                        "deviceSupportDataRead"
                    ) < 0
                ) {
                    errorMsg = 'User not authorized for "View Device Vendor Support Data"';
                    errorCode = "";
                }
                if (err === "NoOS") {
                    this._logger.warn(
                        "No Os Version available",
                        "AdvisoryViewComponent.whenModelIsReady() error"
                    );
                } else {
                    this._logger.warn(err, "AdvisoryViewComponent.whenModelIsReady() error");
                }
                //NotAuthorized
                if (typeof err !== "string" || err !== "NoOS") {
                    this._modalUserFeedback.displayError({
                        errorMsg: errorMsg,
                        errorCode: errorCode,
                    });
                }

                return Promise.reject(err);
            });
    }

    retrieveVendorOsMap() {
        return this._vendorApiConfig.loadAndMergeNistVendorOsMap(this._gameEditor._activeOrgId);
    }

    /**
     * Whether the tenant holds the Titan/EM license the catalog-freshness chip depends
     * on. Advisory Summary itself works without Titan (Cisco/NIST feeds), so unlicensed
     * orgs must skip this chip rather than show a sync stat that can never populate.
     * @returns {Promise<Boolean>}
     */
    isCatalogFreshnessLicensed() {
        return applyExposureLicenseGate({
            licenseRestriction: this._gameEditor._licenseRestriction,
            logger: this._logger,
            hide: () => {},
        }).then((result) => result.licensed);
    }

    /**
     * Best-effort read of the advisory-catalog freshness facts: the newest
     * per-vendor last-SUCCESSFUL support-data sync (ExposureManagementPrefs
     * .lastSupportDataSync) and the org window (advisoryStaleDays). Resolves
     * null on any failure — the renderer then shows the chip as unknown.
     * @returns {Promise<{advisorySyncAt: Number|null, advisoryStaleDays: Number|undefined}|null>}
     */
    loadCatalogFreshness() {
        // ExposureManagementPrefs is a tenant singleton, so the entity's own loader takes
        // the org id and resolves the row by it.
        return ExposureManagementPrefsEntity.loadExposureManagementPrefs(
            this._gameEditor._activeOrgId,
            {id: true, advisoryStaleDays: true, lastSupportDataSync: true}
        )
            .then((prefs) => {
                if (!prefs) {
                    return null;
                }
                // advisoryStaleDays reads through the generated getter. lastSupportDataSync
                // does NOT have one: the prop is newer than the published SDK, so it is
                // absent from this entity's $filterProperties and no accessor was generated
                // for it. Nothing strips undeclared props off a loaded row, so the value is
                // still there in the pristine bag. Move it to prefs.lastSupportDataSync once
                // an SDK carrying the prop ships.
                const pristine =
                    typeof prefs.getPristineProperties === "function"
                        ? prefs.getPristineProperties()
                        : prefs;
                const stamps = (pristine && pristine.lastSupportDataSync) || {};
                let advisorySyncAt = null;
                Object.keys(stamps).forEach((vendor) => {
                    const at = stamps[vendor] && stamps[vendor].lastSuccessAt;
                    if (
                        typeof at === "number" &&
                        (advisorySyncAt === null || at > advisorySyncAt)
                    ) {
                        advisorySyncAt = at;
                    }
                });
                return {advisorySyncAt, advisoryStaleDays: prefs.advisoryStaleDays};
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "AdvisoryViewComponent - could not load catalog freshness (chip reads unknown)"
                );
                return null;
            });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onUserSorted(sortData) {
        sortData = [].concat(sortData);
        let sort = {};

        sortData.forEach((sortData) => {
            if (!sortData || !sortData.sortColumn || !this.columnConfig[sortData.sortColumn]) {
                return;
            }
            const sortCriteria =
                this.columnConfig[sortData.sortColumn].valuePath ||
                this.columnConfig[sortData.sortColumn].path ||
                sortData.sortColumn;
            sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        });

        sort = this._patchSorting(sort);

        this._pagingFeed.setSort(sort);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    getAdvisoryInListById(id) {
        let inst = null;
        if (this._pagingFeed) {
            inst = this._pagingFeed.getInstanceById(id);
        }
        return inst;
    }

    async getAdvisoryList() {
        if (this._entity && this._entity.loadRelatedAdvisories) {
            const colConfig = {
                "data.advisoryId": true,
                "data.advisoryTitle": true,
                "data.bugIDs": true,
                "data.cves": true,
                "data.firstFixed": true,
                "data.firstPublished": true,
                "data.sir": true,
                "data.summary": true,
                displayName: true,
                readMore: true,
                source: true,
            };
            return await this._entity.loadRelatedAdvisories({}, colConfig, {createdOn: 1}, false);
        }
        return this._pagingFeed.cache;
    }

    getPagingFeed() {
        return this.pagingFeed;
    }

    get pagingFeed() {
        return this._pagingFeed;
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = AdvisoryViewComponent;

AdvisoryViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.io",
    "$teve.utils",
    "$teve.userSession",
    "$teve.storageFactory",
    "$teve.solutionsManager",
    "$teve.ui.userFeedback",
    "$teve.entities.VendorApiConfig",
];

AdvisoryViewComponent.$displayDetails = {
    type: "View",
    name: "Advisory Summary",
    icon: "devicemanager.cisco_advisory_list",
    shown: true,
    title: "Advisory Summary",
    order: 5,
};
